import React, { useCallback } from 'react';

import type { ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { isOfflineMode } from '@atlaskit/editor-plugin-connectivity';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIProactivePluginInjectionAPI } from '../../aiProactivePluginType';
import { toggleProactiveAISuggestionDisplayWithAnalytics } from '../../pm-plugins/ai-proactive/commands-with-analytics';
import type { AIGlobalOptIn } from '../../types';
import { PrimaryToolbarButton } from '../components/PrimaryToolbarButton/PrimaryToolbarButton';
// Remove below line while cleaning platform_editor_ai_object_sidebar_injection

type PrimaryToolbarComponentType = {
	aiGlobalOptIn: AIGlobalOptIn;
	api: AIProactivePluginInjectionAPI;
	disabled: boolean;
	editorView: EditorView;
	// Make toggleSuggestionsPanel mandatory prop while cleaning platform_editor_ai_object_sidebar_injection
	toggleSuggestionsPanel?: (triggeredFrom: string) => void;
};

const PrimaryToolbarComponent = ({
	api,
	toggleSuggestionsPanel,
	disabled,
	editorView,
}: PrimaryToolbarComponentType) => {
	const mode = useSharedPluginStateSelector(api, 'connectivity.mode');

	const isProactiveContextPanelOpen = useSharedPluginStateSelector(
		api,
		'aiProactive.isProactiveContextPanelOpen',
	);
	const showNewRecommendationsNotification = useSharedPluginStateSelector(
		api,
		'aiProactive.showNewRecommendationsNotification',
	);
	const unreadRecommendationCount = useSharedPluginStateSelector(
		api,
		'aiProactive.unreadRecommendationCount',
	);

	const onClick = useCallback(() => {
		// Ignored via go/ees007
		// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
		// TODO: handle aiGlobalOptIn correctly
		// Make toggleSuggestionsPanel will be mandatory prop after platform_editor_ai_object_sidebar_injection is cleaned
		//	so no need to check for toggleSuggestionsPanel
		if (toggleSuggestionsPanel && fg('platform_editor_ai_object_sidebar_injection')) {
			toggleSuggestionsPanel('mainToolbar');
		} else {
			const { state, dispatch } = editorView;
			toggleProactiveAISuggestionDisplayWithAnalytics(api?.analytics?.actions)('mainToolbar')(
				state,
				dispatch,
			);
		}
	}, [api?.analytics?.actions, editorView, toggleSuggestionsPanel]);

	const showLoading = !isProactiveContextPanelOpen && !!showNewRecommendationsNotification;

	const isOffline = isOfflineMode(mode);

	return (
		<PrimaryToolbarButton
			api={api}
			onClick={onClick}
			disabled={disabled || isOffline}
			selected={!!isProactiveContextPanelOpen}
			loading={showLoading}
			unreadCount={unreadRecommendationCount}
		/>
	);
};

export const getAIPrimaryToolbarComponent =
	({
		api,
		aiGlobalOptIn,
		toggleSuggestionsPanel,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		api: AIProactivePluginInjectionAPI;
		toggleSuggestionsPanel: (triggeredFrom: string) => void;
	}): ToolbarUIComponentFactory =>
	({ editorView, disabled }) => {
		if (!editorView) {
			return null;
		}

		return (
			<PrimaryToolbarComponent
				editorView={editorView}
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				disabled={disabled}
				toggleSuggestionsPanel={toggleSuggestionsPanel}
			/>
		);
	};

// Remove below function while cleaning platform_editor_ai_object_sidebar_injection
export const getAIPrimaryToolbarComponentOld =
	({
		api,
		aiGlobalOptIn,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		api: AIProactivePluginInjectionAPI;
	}): ToolbarUIComponentFactory =>
	({ editorView, disabled }) => {
		if (!editorView) {
			return null;
		}

		return (
			<PrimaryToolbarComponent
				editorView={editorView}
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				disabled={disabled}
			/>
		);
	};
